import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

import Layout, { PageHeader, Surface } from "@/components/Layout";
import { api } from "@/lib/api";

const COLORS = ["#09090B", "#2563EB", "#10B981", "#F59E0B", "#EF4444", "#A855F7"];

export default function Analytics() {
  const [revenue, setRevenue] = useState({ series: [], top_merchants: [] });
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [tx, setTx] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/admin/revenue?days=30"),
      api.get("/admin/stats"),
      api.get("/admin/merchants"),
      api.get("/admin/transactions?limit=500"),
    ]).then(([r, s, m, t]) => { setRevenue(r.data); setStats(s.data); setMerchants(m.data.items); setTx(t.data.items); });
  }, []);

  const byId = Object.fromEntries(merchants.map(m => [m.id, m.company_name]));
  const txByType = tx.reduce((acc, t) => { acc[t.type] = (acc[t.type] || 0) + 1; return acc; }, {});
  const pie = Object.entries(txByType).map(([k, v]) => ({ name: k, value: v }));

  return (
    <Layout>
      <div className="p-6 lg:p-10" data-testid="analytics-page">
        <PageHeader title="Analytics" subtitle="Revenue, volume, programme mix." />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Surface className="lg:col-span-2 p-5">
            <div className="font-display text-xl tracking-tight font-medium mb-4">Revenue trend (30d)</div>
            <div className="h-72">
              <ResponsiveContainer>
                <AreaChart data={revenue.series} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity={0.18} />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#F4F4F5" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                  <YAxis tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                  <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} />
                  <Area dataKey="revenue" type="monotone" stroke="#2563EB" strokeWidth={1.5} fill="url(#revx)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Surface>

          <Surface className="p-5">
            <div className="font-display text-xl tracking-tight font-medium mb-4">Transaction mix</div>
            <div className="h-72">
              {pie.length === 0 ? (
                <div className="text-xs text-zinc-500 h-full flex items-center justify-center">No transactions yet.</div>
              ) : (
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pie} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50} stroke="none">
                      {pie.map((entry, i) => <Cell key={`tx-type-${entry.name}`} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </Surface>
        </div>

        <Surface className="p-5">
          <div className="font-display text-xl tracking-tight font-medium mb-4">Top merchants by revenue</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart
                data={(revenue.top_merchants || []).map(m => ({
                  name: byId[m.merchant_id]?.slice(0, 18) || m.merchant_id.slice(0, 6),
                  revenue: m.revenue,
                }))}
                margin={{ top: 5, right: 8, left: -12, bottom: 0 }}
              >
                <CartesianGrid stroke="#F4F4F5" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                <YAxis tick={{ fontSize: 10, fill: "#71717A" }} stroke="#E4E4E7" />
                <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E4E4E7" }} />
                <Bar dataKey="revenue" fill="#09090B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Surface>
      </div>
    </Layout>
  );
}
